import { useState, useEffect } from "react";
import {
  FaCalendarCheck,
  FaUsers,
  FaWrench,
  FaClock,
  FaExclamationTriangle,
  FaCheckCircle,
  FaArrowLeft,
} from "react-icons/fa";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../components/Header";
import { CustomerInformationSection } from "./sections/CustomerInformationSection";
import { TIcketInformationSection } from "./sections/TicketInformationSection";
import { TicketTitleSection } from "./sections/TicketTitleSection";
import { TicketScheduleSection } from "./sections/TicketScheduleSection";
import { TicketUpdateProgressAction } from "./actions/UpdateProgress";
import { TicketRescheduleAction } from "./actions/Reschedule";
import { useHookTicketDetail } from "../../../../hooks/useHookTicketDetail";
import { ActionButtonSection } from "./sections/ActionButtonSection";
import { TicketTakenAction } from "./actions/TicketTacken";

/* ============================================
   Dummy Data (Menyesuaikan Model Laravel)
   ============================================ */
const DUMMY_TICKET = {
  id: "9d2e1f80-a1b2-4c3d-8e5f-1234567890ab",
  code: "TKT-X8A9B2C1",
  title: "Internet Mati Total Sejak Kemarin",
  description:
    "Pelanggan melaporkan bahwa koneksi internet mati total sejak pukul 19:00 WIB kemarin. Lampu indikator PPPoE pada router berwarna merah terus menerus.",
  note: "Pelanggan sudah mencoba restart router namun tidak ada perubahan. Kemungkinan ada putus kabel di jalur distribusi atau sisi ODP.",
  status: {
    id: "status-2",
    name: "Dalam Pengerjaan",
    color: "#F59E0B", // Amber
  },
  pppoeUser: {
    id: "pppoe-1",
    name: "Budi Santoso",
    username: "budi.santoso@isp.net",
    ip_address: "192.168.10.15",
    address: "Jl. Merpati No. 45, RT 03/RW 05, Jakarta Selatan",
  },
  technicians: [
    {
      id: "tech-1",
      name: "Andi Pratama",
      phone: "0812-3456-7890",
      role: "Senior Teknisi",
    },
    {
      id: "tech-2",
      name: "Siti Aminah",
      phone: "0878-1234-5678",
      role: "Teknisi Junior",
    },
  ],
  schedules: [
    {
      id: "sch-1",
      date: "2023-10-25",
      time: "09:00",
      status: "Dijadwalkan Ulang",
      reason: "Terjadi hujan deras dan petir, tidak memungkinkan untuk pengecekan kabel di tiang luar.",
    },
    {
      id: "sch-2",
      date: "2023-10-26",
      time: "14:00",
      status: "Dijadwalkan Ulang",
      reason: "Pelanggan tidak ada di rumah dan tidak bisa dihubungi.",
    },
    {
      id: "sch-3",
      date: "2023-10-27",
      time: "10:00",
      status: "Akan Datang",
      reason: null,
    },
  ],
  workDetails: {
    startedAt: "2023-10-27 10:15",
    finishedAt: null,
    progress: 60,
    actions: [
      "Pengecekan kabel dari ODP ke rumah pelanggan",
      "Penggantian konektor SC/UPC yang rusak",
      "Pengetesan sinyal menggunakan OPM (Saat ini sedang berlangsung)",
    ],
  },
};

/* ============================================
   Sub-Komponen: SectionCard
   ============================================ */


/* ============================================
   Sub-Komponen: StatusBadge
   ============================================ */
const StatusBadge = ({ status }) => {
  if (!status) return null;
  return (
    <span
      className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
      style={{
        background: `${status.color}15`,
        color: status.color,
        border: `1px solid ${status.color}30`,
      }}
    >
      {status.name}
    </span>
  );
};

/* ============================================
   Halaman Utama: TicketDetail
   ============================================ */
export const TicketDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Mengambil ID dari URL
  const [showSidebar, setShowSidebar] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [openUpdateProgressModal, setOpenUpdateProgressModal] = useState(false);
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [openTakenModal, setOpenTakenModal] = useState(false);

  const { data, loading, refreshData } = useHookTicketDetail({ ticketID: id });

  useEffect(() => {
    // Simulasi fetch API berdasarkan ID
    setTimeout(() => {
      setTicket(DUMMY_TICKET);
      setIsLoading(false);
    }, 800);
  }, [id]);

  if (isLoading || !ticket) {
    return (
      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <div className="p-6 flex items-center justify-center h-screen">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-10 h-10 rounded-full border-4 border-slate-600 border-t-ocean-400 animate-spin" />
            <p className="text-slate-400">Memuat data tiket...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Detail Tiket {ticket?.code} - Radius Trend</title>
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
            >
              <FaArrowLeft />
            </button>
            <div className="flex flex-col">
              <h2 className="text-lg md:text-xl font-display font-bold text-gray-100 tracking-tight">
                Tiket: {ticket.code}
              </h2>
              <p className="text-xs text-slate-500 hidden md:block mt-0.5">
                Detail laporan dan pengerjaan
              </p>
            </div>
          </div>

          <div className="pl-4 border-l border-slate-700/50">
            <StatusBadge status={ticket.status} />
          </div>
        </Header>
        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-6">
          {/* Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Kolom Kiri (Info Utama) */}
            <div className="lg:col-span-2 space-y-6">

              {/* Info Tiket & Pelanggan */}
              <TicketTitleSection title="Informasi Tiket & Pelanggan" icon={FaExclamationTriangle}>

                <div className="grid md:grid-cols-2 gap-6">
                  <TIcketInformationSection ticket={data} />

                  <CustomerInformationSection ticket={data?.pppoe_user} />

                </div>
              </TicketTitleSection>

              {/* Detail Pengerjaan */}
              <TicketTitleSection title="Detail Pengerjaan" icon={FaWrench}>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-400">
                      <FaClock className="text-emerald-400" />
                      <span>Mulai: <b className="text-slate-200">{ticket.workDetails.startedAt}</b></span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400">
                      <FaCheckCircle className="text-sky-400" />
                      <span>Selesai: <b className="text-slate-200">{ticket.workDetails.finishedAt || "Belum selesai"}</b></span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs font-semibold text-slate-400">Progress Pengerjaan</span>
                      <span className="text-xs font-bold text-ocean-400">{ticket.workDetails.progress}%</span>
                    </div>
                    <div className="w-full bg-slate-700/50 rounded-full h-2.5">
                      <div
                        className="h-2.5 rounded-full transition-all duration-500"
                        style={{ width: `${ticket.workDetails.progress}%`, background: "linear-gradient(90deg, #0077B6, #00B4D8)" }}
                      ></div>
                    </div>
                  </div>

                  {/* Actions List */}
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">Tindakan Teknisi:</p>
                    <ul className="space-y-2">
                      {ticket.workDetails.actions.map((action, index) => (
                        <li key={index} className="flex items-start gap-3 text-sm text-slate-300">
                          <span className="mt-1 w-5 h-5 rounded-full bg-ocean-600/20 text-ocean-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                            {index + 1}
                          </span>
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </TicketTitleSection>

              {/* Action Button Section */}
              <ActionButtonSection
                setOpenRescheduleModal={setOpenRescheduleModal}
                setOpenUpdateProgressModal={setOpenUpdateProgressModal}
                setOpenTakenModal={setOpenTakenModal}
              />

            </div>

            {/* Kolom Kanan (Sidebar) */}
            <div className="space-y-6">

              {/* Teknisi */}
              <TicketTitleSection title="Teknisi Penugasan" icon={FaUsers}>
                <div className="space-y-3">
                  {data?.teknisis && data?.teknisis.map((tech) => {
                    const teknisi = tech?.teknisi;
                    return (
                      <div
                        key={teknisi?.id}
                        className="flex items-center gap-3 p-3 rounded-xl transition-all"
                        style={{ background: "rgba(15, 23, 42, 0.6)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-ocean-600 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-ocean-900/30">
                          {teknisi?.name?.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-100">{teknisi?.name}</p>
                          <p className="text-xs text-slate-500">{teknisi?.role}</p>
                        </div>
                        {teknisi?.phone_number && (
                          <a
                            href={`https://wa.me/${teknisi?.phone_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-ocean-400 hover:text-ocean-300 font-medium"
                          >
                            Hubungi
                          </a>
                        )}
                      </div>
                    )
                  }

                  )}
                </div>
              </TicketTitleSection>

              {/* Jadwal Kunjungan (Timeline) */}
              <TicketTitleSection title="Riwayat Jadwal" icon={FaCalendarCheck}>
                <TicketScheduleSection data={data?.schedules} />
              </TicketTitleSection>

            </div>
          </div>
        </main>


        <TicketUpdateProgressAction
          isOpen={openUpdateProgressModal}
          onClose={() => setOpenUpdateProgressModal(false)}
          ticket={ticket} // Kirim data tiket yang ada
          onSave={(data) => {
            console.log("Data disimpan:", data);
            // Logic refresh data atau update state bisa di sini
          }}
        />

        <TicketRescheduleAction
          isOpen={openRescheduleModal}
          onClose={() => setOpenRescheduleModal(false)}
          ticket={ticket} // Kirim data tiket yang ada
          onSave={(data) => {
            console.log("Data disimpan:", data);
            // Logic refresh data atau update state bisa di sini
          }}
        />

        <TicketTakenAction
          isOpen={openTakenModal}
          onClose={() => setOpenTakenModal(false)}
          ticket={ticket} // Kirim data tiket yang ada
          onSave={(data) => {
            console.log("Data disimpan:", data);
            // Logic refresh data atau update state bisa di sini
          }}
        />

      </DashboardLayout>
    </>
  );
};